import "./UserLinksLayout.css";
import { ReactElement, useRef, useState } from 'react';
import { Outlet, useNavigate, useParams, Link } from 'react-router-dom';
import { TbLayoutDashboard, TbUserPlus, TbLogin2, TbLink, TbLogout } from "react-icons/tb";
import { Backdrop, SpeedDial, SpeedDialIcon, SpeedDialAction, Button, Typography } from '@mui/material';
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

type Visibility = 'logged' | 'anon' | 'public';

interface MenuAction {
    type: string;
    icon: ReactElement;
    name: string;
    visible: Visibility;
    extended: boolean;
}

const actions: MenuAction[] = [
    { type: 'edit', icon: <TbLayoutDashboard size={20} />, name: 'edit', visible: 'logged', extended: true },
    { type: 'signup', icon: <TbUserPlus size={20} />, name: 'signup', visible: 'anon', extended: true },
    { type: 'login', icon: <TbLogin2 size={20} />, name: 'login', visible: 'anon', extended: true },
    { type: 'copy', icon: <TbLink size={20} />, name: 'copy', visible: 'public', extended: true },
    { type: 'logout', icon: <TbLogout size={20} />, name: 'logout', visible: 'logged', extended: true },
    // TODO: add "share" action (native share API) for mobile, and "copy" for desktop. The copy action is already here, but we can make it more prominent on desktop.
];

const UserLinksLayout = () => {
    const { t } = useTranslation();
    const { user: userAuth } = useAuth() as { user: unknown };
    const { user } = useParams();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [isCopiedText, setIsCopiedText] = useState(false);
    const visibleTo: Visibility = userAuth ? 'logged' : 'anon';
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setTimeout(() => setIsCopiedText(false), 200);
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(`https://linkerto.me/${user}`);
            setIsCopiedText(true);
            if (copyTimerRef.current != null) {
                clearTimeout(copyTimerRef.current);
            }
            copyTimerRef.current = setTimeout(() => setIsCopiedText(false), 5000);
        } catch (err) {
            console.log(err);
        }
    };

    const handleClickOption = (type: string) => {
        switch (type) {
            case 'copy':
                copyLink();
                break;
            case 'login':
                setOpen(false);
                setTimeout(() => navigate("/login"), 200);
                break;
            case 'logout':
                setOpen(false);
                setTimeout(() => navigate("/logout"), 200);
                break;
            case 'signup':
                setOpen(false);
                setTimeout(() => navigate("/signup"), 200);
                break;
            case 'edit':
                setOpen(false);
                setTimeout(() => navigate("/dashboard"), 200);
                break;
            default:
                setOpen(false);
                return;
        }
    };

    // Primary action (copy link) leads the group; the rest follow as icons.
    const visibleActions = actions
        .filter(i => i.visible === visibleTo || i.visible === 'public')
        .sort((a, b) => (a.type === 'copy' ? -1 : b.type === 'copy' ? 1 : 0));
    const actionLabel = (action: MenuAction) =>
        action.type === 'copy' && isCopiedText ? t("nav.copied") : t(`nav.${action.name}`);

    return (
        <div className="user-links-layout">
            <Outlet />
            <>
                <Backdrop open={open} />
                <SpeedDial
                    className='menu-user-float'
                    ariaLabel="User Menu"
                    sx={{ position: 'absolute', bottom: 16, right: 16 }}
                    icon={<SpeedDialIcon />}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    open={open}
                    FabProps={{ color: "primary" }}
                >
                    {visibleActions.map((action) => (
                        <SpeedDialAction
                            className='menu-user-action'
                            key={action.name}
                            icon={action.icon}
                            tooltipTitle={actionLabel(action)}
                            tooltipOpen
                            onClick={() => handleClickOption(action.type)}
                        />
                    ))}
                </SpeedDial>

                <div className="menu-user-drawer-wrapper">
                    <div className="menu-user-drawer">
                        <Link className="linkButton menu-user-drawer-brand" to="/">
                            <Typography variant="h5" color="primary" noWrap component="h5" fontWeight={700}>
                                Linkerto
                            </Typography>
                        </Link>
                        {visibleActions.map((action) => {
                            // Primary (copy) keeps its label as the CTA; the rest
                            // are icon-only secondaries.
                            const isPrimary = action.type === 'copy';
                            return (
                                <Button
                                    key={action.name}
                                    className={`menu-user-drawer-btn ${isPrimary ? 'is-primary' : 'is-secondary'}`}
                                    variant="text"
                                    size="medium"
                                    startIcon={action.icon}
                                    onClick={() => handleClickOption(action.type)}
                                    aria-label={actionLabel(action)}
                                >
                                    <span className="menu-user-drawer-btn-label">{actionLabel(action)}</span>
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </>
        </div>
    );
};

export default UserLinksLayout;
